/**
 * @module deploy/vercel
 * @description SDK-free Vercel deployment slice contract.
 */

import type { DeployAdapter, DeploymentTarget } from "@geenius/adapters";

import {
  createProviderDeployAdapter,
  isProviderDeploymentTarget,
  type ProviderDeployAdapter,
  validateProviderDeploymentTarget,
} from "./provider-slice";

/**
 * Deployment target accepted by the Vercel provider slice.
 */
export type VercelDeploymentTarget = DeploymentTarget & {
  readonly provider: "vercel";
};

/**
 * Vercel-scoped deployment adapter. Downstream wiring may accept a neutral
 * adapter, but the wrapped public slice only accepts Vercel targets.
 */
export type VercelDeployAdapter = ProviderDeployAdapter<"vercel">;

/**
 * SDK-free manifest describing Vercel deployment capabilities and required
 * environment variables.
 */
export interface VercelDeployProviderManifest {
  readonly provider: "vercel";
  readonly displayName: string;
  readonly runtime: "sdk-free-contract";
  readonly capabilities: readonly string[];
  readonly environmentVariables: readonly string[];
}

/**
 * Vercel deploy contract bundle with its manifest, wrapped adapter, and target
 * validator.
 */
export interface VercelDeployContract {
  readonly provider: "vercel";
  readonly manifest: VercelDeployProviderManifest;
  readonly adapter: VercelDeployAdapter;
  validateTarget(target: unknown): VercelDeploymentTarget;
}

/**
 * Provider manifest for SDK-free Vercel deployment adapters.
 */
export const VERCEL_DEPLOY_PROVIDER: VercelDeployProviderManifest = {
  provider: "vercel",
  displayName: "Vercel",
  runtime: "sdk-free-contract",
  capabilities: [
    "project-deployments",
    "rollback",
    "deployment-lookup",
    "deployment-list",
    "health-check",
  ],
  environmentVariables: ["VERCEL_PROJECT_ID", "VERCEL_TOKEN"],
};

/**
 * Checks whether a deployment target belongs to the Vercel provider.
 */
export function isVercelDeploymentTarget(
  target: unknown,
): target is VercelDeploymentTarget {
  return isProviderDeploymentTarget("vercel", target);
}

/**
 * Validates and narrows a deployment target for the Vercel provider slice.
 */
export function validateVercelDeploymentTarget(
  target: unknown,
): VercelDeploymentTarget {
  return validateProviderDeploymentTarget("vercel", target);
}

/**
 * Wraps a generic deployment adapter with Vercel target and result guards.
 */
export function createVercelDeployAdapter(
  adapter: DeployAdapter,
): VercelDeployAdapter {
  return createProviderDeployAdapter({ provider: "vercel", adapter });
}

/**
 * Creates a Vercel deploy contract bundle from a provider-neutral adapter.
 */
export function createVercelDeployContract(
  adapter: DeployAdapter,
): VercelDeployContract {
  return {
    provider: "vercel",
    manifest: VERCEL_DEPLOY_PROVIDER,
    adapter: createVercelDeployAdapter(adapter),
    validateTarget: validateVercelDeploymentTarget,
  };
}
